import { Link } from 'react-router-dom';

import { colors } from '../../../colors';
import {
  AccountIcon,
  CalcIcon,
  ChevronRightIcon,
  ControlIcon,
  LogoutIcon,
} from './icons';

export interface Identity {
  name: string;
  email: string;
  initial: string;
}

export interface Credits {
  used: number;
  total: number;
}

export type ExchangeStatus = 'ok' | 'warn' | 'down';

export interface Exchange {
  name: string;
  status: ExchangeStatus;
}

// Operator pages — dev-only, surfaced inside the dropdown.
const OPERATOR_PAGES = [
  { label: 'Control', href: '/control', Icon: ControlIcon },
  { label: 'Profit Calculations', href: '/profitCalculations', Icon: CalcIcon },
] as const;

const exchangeDotColor = (status: ExchangeStatus): string =>
  status === 'ok' ? colors.green : status === 'warn' ? colors.gold : colors.red;

interface Props {
  isDev: boolean;
  isDemo: boolean;
  /** True when a real user was logged in before entering demo mode (so exiting
   *  demo returns them to their account rather than the Login screen). */
  wasLoggedIn: boolean;
  identity: Identity;
  credits: Credits;
  exchanges: Exchange[];
  onLogout: () => void;
  onEnterDemo: () => void;
  onExitDemo: () => void;
}

/** Profile menu opened from the avatar. Becomes a full-width sheet on mobile. */
const ProfileDropdown = ({
  isDev,
  isDemo,
  wasLoggedIn,
  identity,
  credits,
  exchanges,
  onLogout,
  onEnterDemo,
  onExitDemo,
}: Props) => (
  <div className="apex-drop" role="menu">
    <div className="apex-drop-header">
      <span className="apex-avatar apex-avatar--lg">{identity.initial}</span>
      <div className="apex-drop-id">
        <div className="apex-drop-name">{identity.name}</div>
        {identity.email && (
          <div className="apex-drop-email">{identity.email}</div>
        )}
      </div>
    </div>

    {isDev && (
      <>
        <div className="apex-drop-label">Operator pages</div>
        {OPERATOR_PAGES.map((page) => {
          const { Icon } = page;
          return (
            <Link
              key={page.href}
              to={page.href}
              className="apex-drop-row"
              role="menuitem"
            >
              <span className="apex-drop-row-icon">
                <Icon size={18} />
              </span>
              <span>{page.label}</span>
              <span className="apex-drop-row-chev">
                <ChevronRightIcon size={14} />
              </span>
            </Link>
          );
        })}
        <div className="apex-drop-divider" />
      </>
    )}

    {!isDemo && (
      <>
        <div className="apex-drop-label">Account</div>
        <Link to="/account" className="apex-drop-row" role="menuitem">
          <span className="apex-drop-row-icon">
            <AccountIcon size={18} />
          </span>
          <span>My Account</span>
          <span className="apex-drop-row-chev">
            <ChevronRightIcon size={14} />
          </span>
        </Link>

        <div className="apex-drop-divider" />
      </>
    )}

    <div className="apex-drop-credits">
      <div className="apex-drop-credits-row">
        <span className="apex-drop-credits-label">Credits</span>
        <span className="apex-drop-credits-num">
          {credits.used}
          <span className="apex-drop-credits-total"> / {credits.total}</span>
        </span>
      </div>
      <div className="apex-drop-credit-bar">
        <div
          className="apex-drop-credit-fill"
          style={{ width: `${(credits.used / credits.total) * 100}%` }}
        />
      </div>
    </div>

    <div className="apex-drop-divider" />

    <div className="apex-drop-label">Exchanges</div>
    <div className="apex-drop-exchanges">
      {exchanges.map((ex) => (
        <div key={ex.name} className="apex-drop-ex">
          <span
            className="apex-drop-ex-dot"
            style={{ background: exchangeDotColor(ex.status) }}
          />
          <span>{ex.name}</span>
        </div>
      ))}
    </div>

    <div className="apex-drop-divider" />

    {isDev && !isDemo && (
      <button
        type="button"
        className="apex-drop-row"
        role="menuitem"
        onClick={onEnterDemo}
      >
        <span className="apex-drop-row-icon">
          <AccountIcon size={18} />
        </span>
        <span>Demo Account</span>
      </button>
    )}

    <button
      type="button"
      className="apex-drop-row apex-drop-logout"
      role="menuitem"
      onClick={isDemo ? onExitDemo : onLogout}
    >
      <span className="apex-drop-row-icon">
        <LogoutIcon size={18} />
      </span>
      <span>
        {isDemo
          ? wasLoggedIn
            ? 'Return to User Account'
            : 'Log in'
          : 'Log out'}
      </span>
    </button>
  </div>
);

export default ProfileDropdown;
